import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Tooltip from '@mui/material/Tooltip';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InventoryIcon from '@mui/icons-material/Inventory';
import LicenseIcon from '@mui/icons-material/CardMembership';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';

import TableViewIcon from '@mui/icons-material/TableView';
import SettingsIcon from '@mui/icons-material/Settings';
import { useAuth } from '../hooks/useAuth';
import { useNavigationGuard } from '../contexts/NavigationGuardContext';

const DRAWER_WIDTH = 240;
const DRAWER_WIDTH_COLLAPSED = 64;
const SIDEBAR_COLLAPSED_KEY = 'asset-navi-sidebar-collapsed';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  adminOnly?: boolean;
}

const navItems: NavItem[] = [
  { label: '顧客管理', path: '/admin/customers', icon: <BusinessIcon />, adminOnly: true },
  { label: 'ダッシュボード', path: '/', icon: <DashboardIcon /> },
  { label: '資産台帳', path: '/assets', icon: <InventoryIcon /> },
  { label: '契約管理', path: '/contracts', icon: <TableViewIcon /> },
  { label: 'ライセンス管理', path: '/licenses', icon: <LicenseIcon /> },
  { label: 'ユーザー管理', path: '/admin/users', icon: <PeopleIcon />, adminOnly: true },
  { label: '基本情報マスタ', path: '/admin/master', icon: <SettingsIcon />, adminOnly: true },
];

const Layout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  // Desktop sidebar collapsed (icon-only rail) state, persisted so the user's
  // preference survives navigation and reloads.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const { user, logout } = useAuth();
  const { checkGuards } = useNavigationGuard();
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleToggleCollapse = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const currentDrawerWidth = collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavClick = async (path: string) => {
    const allowed = await checkGuards(path);
    if (!allowed) return;
    navigate(path);
    setMobileOpen(false);
  };

  const filteredNavItems = navItems.filter(
    (item) => !item.adminOnly || user?.role === 'admin',
  );

  // `isCollapsed` only applies to the desktop drawer; the mobile temporary
  // drawer always renders in full (expanded) form.
  const renderDrawer = (isCollapsed: boolean) => (
    <div>
      <Toolbar variant="dense" sx={{ minHeight: 48 }}>
        {!isCollapsed && (
          <Typography variant="h6" noWrap sx={{ fontFamily: '"Meiryo", "メイリオ", sans-serif', fontWeight: 'bold' }}>
            <span style={{ color: '#1B3A6B' }}>Cell</span><span style={{ color: '#2E8B7A' }}>Box</span>
          </Typography>
        )}
      </Toolbar>
      <Divider />
      <List>
        {filteredNavItems.map((item) => (
          <ListItem key={item.path} disablePadding sx={{ display: 'block' }}>
            <Tooltip title={isCollapsed ? item.label : ''} placement="right">
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => handleNavClick(item.path)}
                sx={{ justifyContent: isCollapsed ? 'center' : 'flex-start', px: isCollapsed ? 1.5 : 2, height: 48, minHeight: 48, maxHeight: 48, boxSizing: 'border-box', py: 0 }}
              >
                <ListItemIcon sx={{ minWidth: 0, mr: isCollapsed ? 0 : 2, justifyContent: 'center' }}>
                  {item.icon}
                </ListItemIcon>
                {!isCollapsed && <ListItemText primary={item.label} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>
      <Divider />
      {/* Collapse/expand toggle at the bottom of the sidebar */}
      <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: isCollapsed ? 'center' : 'flex-end', p: 1 }}>
        <Tooltip title={isCollapsed ? '開く' : '閉じる'} placement="right">
          <IconButton
            onClick={handleToggleCollapse}
            size="small"
            aria-label={isCollapsed ? 'サイドバーを開く' : 'サイドバーを閉じる'}
          >
            {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Tooltip>
      </Box>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          backgroundColor: '#EBF2FA',
          color: '#1B3A6B',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        }}
      >
        <Toolbar variant="dense" sx={{ minHeight: 48 }}>
          <IconButton
            color="inherit"
            aria-label="メニューを開く"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontFamily: '"Meiryo", "メイリオ", sans-serif', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <img src="/favicon.svg" alt="" width={28} height={28} style={{ display: 'block' }} />
            <span><span style={{ color: '#1B3A6B' }}>Cell</span><span style={{ color: '#2E8B7A' }}>Box</span></span>
          </Typography>
          {user && (
            <Typography variant="body2" sx={{ mr: 2 }}>
              {user.name}
            </Typography>
          )}
          <Button color="inherit" onClick={handleLogout}>
            ログアウト
          </Button>
        </Toolbar>
      </AppBar>

      {/* Mobile drawer (always full width) */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
        }}
      >
        {renderDrawer(false)}
      </Drawer>

      {/* Desktop drawer (collapsible) */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: currentDrawerWidth,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: currentDrawerWidth,
            overflowX: 'hidden',
            transition: (t) => t.transitions.create('width', {
              easing: t.transitions.easing.sharp,
              duration: t.transitions.duration.enteringScreen,
            }),
          },
        }}
        open
      >
        {renderDrawer(collapsed)}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // In a flex row, a flex item defaults to `min-width: auto`, which
          // prevents it from shrinking below its content's intrinsic width.
          // A very wide child (the spreadsheet) would then push the whole
          // page wider and cause a horizontal scrollbar on <body>. Setting
          // `minWidth: 0` lets this item shrink to the available space so any
          // horizontal overflow stays inside the child (the spreadsheet).
          minWidth: 0,
          overflowX: 'hidden',
          p: 3,
          mt: '48px',
          transition: (t) => t.transitions.create(['width', 'margin'], {
            easing: t.transitions.easing.sharp,
            duration: t.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;
